import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, Chip, Collapse, IconButton, Tooltip, Typography } from '@mui/material'
import {
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import { EditorJsField } from '@/components/editor/EditorJsField'
import {
  useProgramElementRealtime,
  type ProgramElementCompletedPayload,
} from '@/realtime/useProgramElementRealtime'
import type { AssignedThemeElement } from '@/api/programs'
import type { EditorJsData } from '@/types'

interface Props {
  elements: AssignedThemeElement[]
  /** Передан — рядом с блоком появляется отметка «пройдено» (действие учителя) */
  onToggleComplete?: (element: AssignedThemeElement, completed: boolean) => void
  /**
   * Развернуть блоки сразу. На уроке программа — не оглавление, а материал, по
   * которому идёт занятие: сворачивать его в список заголовков значит прятать
   * то, ради чего блок и открыт.
   */
  defaultExpanded?: boolean
  /** Передан — у словаря темы появляется кнопка «Добавить в урок» */
  onAddWordsToLesson?: (element: AssignedThemeElement) => void
  /** Передан — у блока появляется меню правки копии, назначенной ученику */
  onEditElement?: (element: AssignedThemeElement) => void
  onDeleteElement?: (element: AssignedThemeElement) => void
  disabled?: boolean
}

/**
 * Содержимое темы: блоки теории раскрываются и читаются целиком, словарь темы
 * показывает слова с переводом.
 *
 * Общий компонент для «Моей программы» и блока программы на уроке: без него
 * ученик видел только заголовки блоков и прочитать теорию не мог нигде.
 */
export function ThemeElementList({
  elements,
  onToggleComplete,
  defaultExpanded = false,
  onAddWordsToLesson,
  onEditElement,
  onDeleteElement,
  disabled = false,
}: Props) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState<number[]>([])
  // Editor.js создаётся только после того, как раскрытие закончилось: внутри
  // анимации контейнер ещё перестраивается, и редактор молча остаётся пустым.
  const [ready, setReady] = useState<number[]>([])

  /**
   * Отметки «пройдено», приехавшие по сокету, поверх того, что отдал GET.
   *
   * Отдельным состоянием, а не правкой пропса: список приходит сверху и
   * перезапрашивается по своим поводам, и писать в чужие данные было бы
   * враньём. В карте лежит только то, что реально разошлось с сервером.
   */
  const [completedOverrides, setCompletedOverrides] = useState<Record<number, string | null>>({})

  /**
   * Блоки, которые уже раскрывались автоматически.
   *
   * Без этой памяти автораскрытие спорит с любым сворачиванием: список тем на
   * уроке перечитывается после каждого события, эффект видит «блок не раскрыт»
   * и распахивает его обратно. То есть блок, свёрнутый по отметке «пройдено»
   * (и блок, свёрнутый руками), через секунду открывался бы сам.
   */
  const autoExpandedRef = useRef<number[]>([])

  // Список приходит асинхронно, поэтому раскрытие по умолчанию — эффектом, а
  // не начальным состоянием: на первом рендере элементов ещё нет
  useEffect(() => {
    if (!defaultExpanded) return

    const missing = elements
      .map((element) => element.id)
      .filter((id) => !autoExpandedRef.current.includes(id))

    if (missing.length === 0) return

    autoExpandedRef.current = [...autoExpandedRef.current, ...missing]
    setExpanded((current) => [...current, ...missing.filter((id) => !current.includes(id))])
  }, [defaultExpanded, elements])

  // Данные с сервера догнали событие — держать поверх них нечего
  useEffect(() => {
    setCompletedOverrides((current) => {
      if (Object.keys(current).length === 0) return current

      const next = { ...current }
      let changed = false

      for (const element of elements) {
        if (element.id in next && next[element.id] === element.completed_at) {
          delete next[element.id]
          changed = true
        }
      }

      return changed ? next : current
    })
  }, [elements])

  /**
   * Учитель отметил блок пройденным: применяем отметку и сворачиваем блок.
   *
   * Сворачивание — часть самой отметки: пройденный материал занимает экран
   * ученика ровно до тех пор, пока по нему идут. Событие может прийти и по
   * блоку из соседней темы — такой отметке в этом списке просто нечего
   * применить, и она молча проходит мимо.
   */
  const handleCompleted = useCallback((payload: ProgramElementCompletedPayload) => {
    setCompletedOverrides((current) =>
      current[payload.element_id] === payload.completed_at
        ? current
        : { ...current, [payload.element_id]: payload.completed_at },
    )

    if (payload.completed_at === null) return

    setExpanded((current) =>
      current.includes(payload.element_id) ? current.filter((id) => id !== payload.element_id) : current,
    )
  }, [])

  useProgramElementRealtime(handleCompleted)

  const toggle = (id: number) => {
    // Раскрыл или свернул руками — автораскрытие сюда больше не лезет
    if (!autoExpandedRef.current.includes(id)) autoExpandedRef.current = [...autoExpandedRef.current, id]

    setExpanded((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]))
  }

  /** Состояние отметки: событие по сокету свежее того, что успел отдать GET */
  const completedAt = (element: AssignedThemeElement): string | null =>
    element.id in completedOverrides ? completedOverrides[element.id] : element.completed_at

  // Подпись типа не подставляем: «Теория» над куском теории и «Словарь темы»
  // над плашками слов ничего не добавляют — что это за блок, видно по нему
  // самому. Свой заголовок учителя показываем как есть.
  const elementTitle = (element: AssignedThemeElement) => element.title?.trim() || null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {elements.map((element) => {
        const isOpen = expanded.includes(element.id)
        const isCompleted = completedAt(element) !== null
        const hasBody = element.type === 'theory' && Boolean(element.body_json)
        const hasWords = element.type === 'vocabulary' && (element.words?.length ?? 0) > 0
        const canExpand = hasBody || hasWords

        return (
          <Box key={element.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {onToggleComplete ? (
                <Tooltip
                  title={
                    isCompleted
                      ? t('programs.my.markNotDone', 'Снять отметку «пройдено»')
                      : t('programs.my.markDone', 'Отметить пройденным')
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      disabled={disabled}
                      onClick={() => onToggleComplete(element, !isCompleted)}
                    >
                      {isCompleted ? (
                        <CheckCircleIcon fontSize="small" color="success" />
                      ) : (
                        <RadioButtonUncheckedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              ) : isCompleted ? (
                <CheckCircleIcon
                  fontSize="small"
                  color="success"
                  aria-label={t('programs.my.done', 'пройдено')}
                />
              ) : (
                <RadioButtonUncheckedIcon
                  fontSize="small"
                  color="disabled"
                  aria-label={t('programs.my.notDone', 'не пройдено')}
                />
              )}

              <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                {elementTitle(element)}
              </Typography>

              {element.type === 'vocabulary' && (
                <Typography variant="caption" color="text.secondary">
                  {t('programs.wordsCount', '{{count}} слов', { count: element.words?.length ?? 0 })}
                </Typography>
              )}

              {/* Правка копии: подогнать материал под конкретного ученика,
                не трогая шаблон, который проходят остальные */}
              {onEditElement && (
                <Tooltip title={t('common.edit')}>
                  <span>
                    <IconButton size="small" disabled={disabled} onClick={() => onEditElement(element)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {onDeleteElement && (
                <Tooltip title={t('common.delete')}>
                  <span>
                    <IconButton size="small" disabled={disabled} onClick={() => onDeleteElement(element)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {element.type === 'vocabulary' && onAddWordsToLesson && hasWords && (
                <Button size="small" disabled={disabled} onClick={() => onAddWordsToLesson(element)}>
                  {t('programs.lessonBlock.addWordsToLesson')}
                </Button>
              )}

              {canExpand && (
                <IconButton
                  size="small"
                  onClick={() => toggle(element.id)}
                  aria-label={
                    isOpen ? t('common.collapse', 'Свернуть') : t('common.expand', 'Развернуть')
                  }
                >
                  {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              )}
            </Box>

            {canExpand && (
              <Collapse
                in={isOpen}
                unmountOnExit
                onEntered={() => setReady((current) => (current.includes(element.id) ? current : [...current, element.id]))}
                onExited={() => setReady((current) => current.filter((x) => x !== element.id))}
              >
                {/* Без левого отступа: содержимое идёт по той же линии, что
                    заголовок блока и название темы */}
                <Box sx={{ pb: 1 }}>
                  {hasBody && ready.includes(element.id) && (
                    <EditorJsField
                      key={`theme-element-${element.id}`}
                      value={element.body_json as unknown as EditorJsData}
                      onChange={() => {}}
                      readOnly
                      // Тот же набор, что в конструкторе: без него сохранённая
                      // таблица исчезла бы у ученика вместе с содержимым
                      extendedBlocks
                      readOnlySurface="paper"
                      hideLabel
                      initDelayMs={50}
                    />
                  )}

                  {hasWords && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {element.words?.map((word) => (
                        <Chip
                          key={word.id}
                          size="small"
                          variant="outlined"
                          label={word.translation ? `${word.text} — ${word.translation}` : word.text}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Collapse>
            )}
          </Box>
        )
      })}
    </Box>
  )
}
